import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { useLeaderboard } from "@/hooks/useEventLeaderboard";
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  Brain,
  ArrowLeft,
  Clock,
  ChevronDown,
  ChevronLeft,
} from "lucide-react";
import { ensureUsdtSymbol, removeUsdtSuffix } from "@/utils/symbols";
import {
  usePreviousAlerts,
  useLeaderboardAlerts,
  useIncomingListings,
} from "@/hooks/useCryptoAlerts";
import { useTokenMarket, useTokenMetadata } from "@/hooks/useTokenMetadata";
import { TokenMarketStats } from "@/components/analyze/TokenMarketStats";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { LeaderboardTokenTile } from "@/components/LeaderboardTokenTile";
import { IncomingListingTile } from "@/components/IncomingListingTile";
import { TokenIcon } from "@/components/TokenIcon";
import { AlertHistory } from "@/components/AlertHistory";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { PositionTradeDrawer } from "@/components/PositionTradeDrawer";
import { PageHeader } from "@/components/ui/PageHeader";
import { AnalyzeHero, TableCard, ViewAllButton } from "@/components/analyze/AnalyzeSections";

const MOBILE_INITIAL_COUNT = 5;
const MOBILE_MAX_EXPANDED_COUNT = 10;

/**
 * Mobile version of the Analyze page
 * - Single column layout
 * - 3 items + "Show more" for incoming/leaderboards
 * - Back FAB when viewing token analysis
 */
export const AnalyzePageMobile: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const symbolFromUrl = searchParams.get("symbol");
  const [inputSymbol, setInputSymbol] = useState("");
  const [analyzedSymbol, setAnalyzedSymbol] = useState<string | null>(
    symbolFromUrl
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [showAllIncoming, setShowAllIncoming] = useState(false);
  const [showAllGainers, setShowAllGainers] = useState(false);
  const [showAllLosers, setShowAllLosers] = useState(false);

  useEffect(() => {
    if (symbolFromUrl !== analyzedSymbol) {
      setAnalyzedSymbol(symbolFromUrl);
    }
  }, [symbolFromUrl]);

  const handleAnalyzeSymbol = useCallback(
    (symbol: string) => {
      const normalized = ensureUsdtSymbol(symbol.trim().toUpperCase());
      setAnalyzedSymbol(normalized);
      setSearchParams({ symbol: removeUsdtSuffix(normalized) });
    },
    [setSearchParams]
  );

  const handleBackToDashboard = useCallback(() => {
    setAnalyzedSymbol(null);
    setInputSymbol("");
    setSearchParams({});
  }, [setSearchParams]);

  const normalizedSymbol = useMemo(
    () => (analyzedSymbol ? ensureUsdtSymbol(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const baseSymbol = useMemo(
    () => (analyzedSymbol ? removeUsdtSuffix(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const isAnalysisView = !!analyzedSymbol;

  const { data: leaderboardData, isLoading: isLoadingLeaderboard } =
    useLeaderboardAlerts(!isAnalysisView);
  const { data: incomingListings = [], isLoading: isLoadingIncoming } =
    useIncomingListings(!isAnalysisView);

  const {
    sortedGainers,
    sortedLosers,
    pricedGainers,
    pricedLosers,
    isPriceLoading,
    handlePriceUpdate,
  } = useLeaderboard(leaderboardData, isLoadingLeaderboard);

  const visibleGainersIds = useMemo(
    () =>
      new Set(
        pricedGainers
          .slice(
            0,
            showAllGainers ? MOBILE_MAX_EXPANDED_COUNT : MOBILE_INITIAL_COUNT
          )
          .map((a) => a.id)
      ),
    [pricedGainers, showAllGainers]
  );

  const visibleLosersIds = useMemo(
    () =>
      new Set(
        pricedLosers
          .slice(
            0,
            showAllLosers ? MOBILE_MAX_EXPANDED_COUNT : MOBILE_INITIAL_COUNT
          )
          .map((a) => a.id)
      ),
    [pricedLosers, showAllLosers]
  );

  const {
    data: tokenMetadata,
    isLoading: isLoadingMetadata,
    error: metadataError,
  } = useTokenMetadata(baseSymbol, isAnalysisView);

  const { data: previousAlerts = [], isLoading: isLoadingAlerts } =
    usePreviousAlerts(userId, baseSymbol, "", isAnalysisView);

  const { data: exchanges = [] } = useUserExchanges(userId, true);
  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, isAnalysisView);

  const globallyAvailableExchangeCodes = useMemo(() => {
    const allExchanges = tokenAvailability?.userExchanges ?? [];
    return allExchanges.filter(
      (code) => !code.endsWith("-demo") && !code.endsWith("-testnet")
    );
  }, [tokenAvailability?.userExchanges]);

  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );

  const supportedUserExchangeCodes = useMemo(() => {
    return exchanges
      .filter((e) => !e.code.endsWith("-demo") && !e.code.endsWith("-testnet"))
      .map((e) => e.code)
      .filter((code) => globallyAvailableSet.has(code));
  }, [exchanges, globallyAvailableSet]);

  const hasTradableExchange = supportedUserExchangeCodes.length > 0;
  const { data: market } = useTokenMarket(baseSymbol, isAnalysisView);

  const handleAnalyze = useCallback(() => {
    if (!inputSymbol.trim()) return;
    setIsAnalyzing(true);
    handleAnalyzeSymbol(inputSymbol);
    setTimeout(() => setIsAnalyzing(false), 500);
  }, [inputSymbol, handleAnalyzeSymbol]);

  const visibleIncoming = showAllIncoming
    ? incomingListings
    : incomingListings.slice(0, MOBILE_INITIAL_COUNT);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          {!isAnalysisView && (
            <div className="flex flex-col gap-4 [&>*]:shrink-0">
              <PageHeader title="" />
              <AnalyzeHero
                mobile
                value={inputSymbol}
                onChange={setInputSymbol}
                onSubmit={handleAnalyze}
                onPick={handleAnalyzeSymbol}
                busy={isAnalyzing}
              />
              {([
                ["gainers", "Top Gainers", "Highest price increase", "green", <TrendingUp key="i" />, sortedGainers, pricedGainers, visibleGainersIds, showAllGainers, setShowAllGainers],
                ["losers", "Top Losers", "Recent delistings, largest drop", "red", <TrendingDown key="i" />, sortedLosers, pricedLosers, visibleLosersIds, showAllLosers, setShowAllLosers],
              ] as const).map(([kind, title, subtitle, tone, icon, sorted, priced, visible, expanded, setExpanded]) => {
                const rank = new Map([...visible].map((id, i) => [id, i + 1]));
                return (
                  <TableCard
                    key={kind}
                    tone={tone}
                    icon={icon}
                    title={title}
                    subtitle={subtitle}
                    actions={
                      priced.length > MOBILE_INITIAL_COUNT ? (
                        <ViewAllButton expanded={expanded} onClick={() => setExpanded(!expanded)} />
                      ) : undefined
                    }
                  >
                    <div className="border-t border-white/10">
                      {(isLoadingLeaderboard || isPriceLoading) && (
                        <div className="flex justify-center py-6">
                          <Loader2 className="w-6 h-6 animate-spin text-accent" />
                        </div>
                      )}
                      {!isLoadingLeaderboard && !isPriceLoading && visible.size === 0 && (
                        <p className="px-3 py-4 text-sm text-[#a3a3a3]">No listings with price data</p>
                      )}
                      {/* Rows stay mounted so live prices keep streaming; hidden when outside the list. */}
                      {!isLoadingLeaderboard &&
                        [...sorted]
                          .sort((a, b) => (rank.get(a.id) ?? 99) - (rank.get(b.id) ?? 99))
                          .map((alert) => (
                            <div
                              key={`${kind}-${alert.id}`}
                              className={isPriceLoading || !visible.has(alert.id) ? "hidden" : "[&:last-child>div]:border-b-0"}
                            >
                              <LeaderboardTokenTile
                                variant="compact"
                                rank={rank.get(alert.id)}
                                alert={alert}
                                onPriceUpdate={handlePriceUpdate}
                                onAnalyze={handleAnalyzeSymbol}
                              />
                            </div>
                          ))}
                    </div>
                  </TableCard>
                );
              })}

              <TableCard
                tone="amber"
                icon={<Clock />}
                title="Incoming Listings"
                subtitle="Track tokens before they hit the market"
              >
                <div className="flex flex-col gap-3 border-t border-white/10 p-3">
                  {isLoadingIncoming ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin text-accent" />
                    </div>
                  ) : incomingListings.length === 0 ? (
                    <p className="text-sm text-[#a3a3a3]">No incoming listings</p>
                  ) : (
                    visibleIncoming.map((alert, i) => (
                      <IncomingListingTile
                        key={alert.id}
                        variant="card"
                        rank={i + 1}
                        alert={alert}
                        onAnalyze={handleAnalyzeSymbol}
                      />
                    ))
                  )}
                  {incomingListings.length > MOBILE_INITIAL_COUNT && (
                    <button
                      onClick={() => setShowAllIncoming(!showAllIncoming)}
                      className="flex items-center justify-center gap-2 py-2 text-sm font-semibold text-[#d4d4d4]"
                    >
                      {showAllIncoming ? "Show fewer listings" : "View all upcoming listings"}
                      <ChevronDown className={`size-4 transition-transform ${showAllIncoming ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              </TableCard>
            </div>
          )}

          {isAnalysisView && (
            <div className="mb-4 flex items-center gap-3">
              <button
                type="button"
                onClick={handleBackToDashboard}
                aria-label="Back to Analyze"
                className="glass flex size-9 items-center justify-center !rounded-lg"
              >
                <ChevronLeft className="size-5 text-white" />
              </button>
              <h1 className="text-xl font-semibold text-white">Analyze {baseSymbol}</h1>
            </div>
          )}

          {/* Analysis View */}
          {isAnalysisView && (
            <div className="space-y-4">
              {market && <TokenMarketStats market={market} symbol={baseSymbol} />}
              <div className="bg-secondary border border-border rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <TokenIcon
                    symbol={baseSymbol}
                    logo={tokenMetadata?.logo}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-text-primary truncate">
                      {baseSymbol}
                    </h2>
                    {tokenMetadata?.name && (
                      <p className="text-sm text-text-secondary truncate">
                        {tokenMetadata.name}
                      </p>
                    )}
                    {(isLoadingMetadata ||
                      isLoadingAlerts ||
                      isLoadingAvailability) && (
                      <Loader2 className="w-4 h-4 animate-spin text-accent mt-1" />
                    )}
                    {metadataError && !isLoadingMetadata && (
                      <p className="text-xs text-yellow-500">
                        Could not load metadata
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {tokenMetadata?.description && (
                <div className="bg-secondary border border-border rounded-xl p-4">
                  <h3 className="text-sm font-bold text-text-primary mb-2">
                    Description
                  </h3>
                  <p className="text-sm text-text-primary leading-relaxed line-clamp-4">
                    {tokenMetadata.description}
                  </p>
                </div>
              )}

              <AlertHistory alerts={previousAlerts} variant="page-mobile" />

              {globallyAvailableExchangeCodes.length > 0 && (
                <div className="bg-secondary border border-border rounded-xl p-4">
                  <h3 className="text-sm font-bold text-text-primary mb-2">
                    Exchange Availability
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {globallyAvailableExchangeCodes.slice(0, 6).map((code) => (
                      <span
                        key={code}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          supportedUserExchangeCodes.includes(code)
                            ? "bg-green-500/20 text-green-400"
                            : "bg-gray-500/20 text-gray-400"
                        }`}
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                  {hasTradableExchange && (
                    <button
                      onClick={() => setIsTradeDrawerOpen(true)}
                      className="mt-3 w-full py-2 bg-accent hover:bg-accent/80 text-white text-sm font-medium rounded-lg"
                    >
                      Trade
                    </button>
                  )}
                </div>
              )}

              <div className="bg-secondary border border-border rounded-xl p-4">
                <h3 className="text-sm font-bold text-text-primary mb-3">
                  Price Chart
                </h3>
                <TradingViewChartWidget
                  key={`chart-${normalizedSymbol}`}
                  symbol={normalizedSymbol}
                />
              </div>

              <div className="bg-secondary border border-border rounded-xl p-4">
                <h3 className="text-sm font-bold text-text-primary flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-accent" />
                  AI Analysis
                </h3>
                <TokenAnalysisSection tokenSymbol={baseSymbol} />
              </div>
            </div>
          )}
        </div>
      </div>

      <PositionTradeDrawer
        userId={userId}
        symbol={normalizedSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={setIsTradeDrawerOpen}
        onGoToPosition={() => {}}
      />

      <ReferralBottomNav />

      {/* Back FAB - only when viewing token analysis */}
      {isAnalysisView && (
        <button
          onClick={handleBackToDashboard}
          className="fixed bottom-24 right-4 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-accent hover:bg-accent/90 text-white shadow-lg transition-all"
          aria-label="Back"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};
